'use client'

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import Player from '@vimeo/player'
import { cn } from '@/lib/utils'
import {
  decodeHtmlEntities,
  extractIframeSrc,
  getEmbedUrl,
  sanitizeEmbedHtml,
} from '@/lib/video/embed'

type Provider = 'youtube' | 'vimeo' | 'loom' | 'unknown'

export type TrackableVideoEmbedRef = {
  play: () => Promise<void> | void
  pause: () => Promise<void> | void
  restart: () => Promise<void> | void
  setMuted: (value: boolean) => Promise<void> | void
  getProvider: () => Provider
}

type Props = {
  url?: string | null
  html?: string | null
  className?: string
  frameClassName?: string
  rounded?: 'xl' | '2xl' | '3xl'
  variant?: 'plain' | 'glass'
  autoplay?: boolean
  muted?: boolean
  controls?: boolean
  startAt?: number
  onReady?: (meta: { provider: Provider }) => void
  onPause?: (seconds: number, duration?: number) => void
  onPlay?: (seconds: number, duration?: number) => void
  onTimeUpdate?: (seconds: number, duration?: number) => void
  onEnded?: () => void
  onDuration?: (seconds: number, label: string) => void
}

declare global {
  interface Window {
    YT?: any
    onYouTubeIframeAPIReady?: () => void
  }
}

function getProvider(src: string): Provider {
  const s = String(src || '').toLowerCase()

  if (
    s.includes('youtube.com') ||
    s.includes('youtu.be') ||
    s.includes('youtube-nocookie.com')
  ) {
    return 'youtube'
  }

  if (s.includes('vimeo.com')) return 'vimeo'
  if (s.includes('loom.com')) return 'loom'

  return 'unknown'
}

function getRoundedClass(rounded: Props['rounded']) {
  if (rounded === '3xl') return 'rounded-3xl'
  if (rounded === 'xl') return 'rounded-xl'
  return 'rounded-2xl'
}

function getYoutubeVideoId(input: string) {
  const raw = String(input || '').trim()
  if (!raw) return ''

  try {
    const u = new URL(raw)
    const host = u.hostname.toLowerCase()
    const path = u.pathname

    if (host.includes('youtu.be')) {
      return path.split('/').filter(Boolean)[0] || ''
    }

    if (host.includes('youtube-nocookie.com')) {
      if (path.startsWith('/embed/')) {
        return path.split('/embed/')[1]?.split('/')[0] || ''
      }
    }

    if (host.includes('youtube.com')) {
      const v = u.searchParams.get('v')
      if (v) return v

      if (path.startsWith('/embed/')) {
        return path.split('/embed/')[1]?.split('/')[0] || ''
      }

      if (path.startsWith('/shorts/')) {
        return path.split('/shorts/')[1]?.split('/')[0] || ''
      }

      if (path.startsWith('/live/')) {
        return path.split('/live/')[1]?.split('/')[0] || ''
      }

      if (path.startsWith('/v/')) {
        return path.split('/v/')[1]?.split('/')[0] || ''
      }
    }
  } catch {}

  return ''
}

function loadYoutubeApi(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('window_unavailable'))
      return
    }

    if (window.YT?.Player) {
      resolve(window.YT)
      return
    }

    const existing = document.querySelector('script[data-youtube-iframe-api="1"]')
    if (existing) {
      const interval = window.setInterval(() => {
        if (window.YT?.Player) {
          window.clearInterval(interval)
          resolve(window.YT)
        }
      }, 100)

      window.setTimeout(() => {
        window.clearInterval(interval)
        if (window.YT?.Player) resolve(window.YT)
        else reject(new Error('youtube_api_timeout'))
      }, 10000)

      return
    }

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.dataset.youtubeIframeApi = '1'

    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT)
    }

    script.onerror = () => reject(new Error('youtube_api_load_failed'))
    document.head.appendChild(script)
  })
}

function formatDurationLabel(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds || 0)))
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60

  if (h > 0) return s > 0 ? `${h}h ${m}m ${s}s` : `${h}h ${m}m`
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`
  return `${s}s`
}

export const TrackableVideoEmbed = forwardRef<TrackableVideoEmbedRef, Props>(
  function TrackableVideoEmbed(
    {
      url,
      html,
      className,
      frameClassName,
      rounded = '2xl',
      variant = 'glass',
      autoplay = false,
      muted = false,
      controls = true,
      startAt = 0,
      onReady,
      onPause,
      onPlay,
      onTimeUpdate,
      onEnded,
      onDuration,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const vimeoPlayerRef = useRef<Player | null>(null)
    const youtubePlayerRef = useRef<any>(null)
    const youtubeTickRef = useRef<number | null>(null)
    const providerRef = useRef<Provider>('unknown')
    const durationRef = useRef<number>(0)

    const onReadyRef = useRef(onReady)
    const onPauseRef = useRef(onPause)
    const onPlayRef = useRef(onPlay)
    const onTimeUpdateRef = useRef(onTimeUpdate)
    const onEndedRef = useRef(onEnded)
    const onDurationRef = useRef(onDuration)

    useEffect(() => {
      onReadyRef.current = onReady
      onPauseRef.current = onPause
      onPlayRef.current = onPlay
      onTimeUpdateRef.current = onTimeUpdate
      onEndedRef.current = onEnded
      onDurationRef.current = onDuration
    }, [onReady, onPause, onPlay, onTimeUpdate, onEnded, onDuration])

    const { src, provider } = useMemo(() => {
      if (url && String(url).trim()) {
        const embed = getEmbedUrl(url)
        return {
          src: embed,
          provider: getProvider(embed),
        }
      }

      const decoded = html ? decodeHtmlEntities(String(html)) : ''
      const safe = sanitizeEmbedHtml(decoded)
      const iframeSrc = extractIframeSrc(safe)
      const embed = iframeSrc ? getEmbedUrl(iframeSrc) : ''

      return {
        src: embed,
        provider: getProvider(embed),
      }
    }, [url, html])

    useImperativeHandle(ref, () => ({
      play: async () => {
        if (providerRef.current === 'vimeo' && vimeoPlayerRef.current) {
          try {
            await vimeoPlayerRef.current.play()
          } catch {}
          return
        }

        if (providerRef.current === 'youtube' && youtubePlayerRef.current?.playVideo) {
          try {
            youtubePlayerRef.current.playVideo()
          } catch {}
        }
      },
      pause: async () => {
        if (providerRef.current === 'vimeo' && vimeoPlayerRef.current) {
          try {
            await vimeoPlayerRef.current.pause()
          } catch {}
          return
        }

        if (providerRef.current === 'youtube' && youtubePlayerRef.current?.pauseVideo) {
          try {
            youtubePlayerRef.current.pauseVideo()
          } catch {}
        }
      },
      restart: async () => {
        if (providerRef.current === 'vimeo' && vimeoPlayerRef.current) {
          try {
            await vimeoPlayerRef.current.setCurrentTime(0)
            await vimeoPlayerRef.current.play()
          } catch {}
          return
        }

        if (providerRef.current === 'youtube' && youtubePlayerRef.current?.seekTo) {
          try {
            youtubePlayerRef.current.seekTo(0, true)
            youtubePlayerRef.current.playVideo?.()
          } catch {}
        }
      },
      setMuted: async (value: boolean) => {
        if (providerRef.current === 'vimeo' && vimeoPlayerRef.current) {
          try {
            await vimeoPlayerRef.current.setVolume(value ? 0 : 1)
          } catch {}
          return
        }

        if (providerRef.current === 'youtube' && youtubePlayerRef.current) {
          try {
            if (value) youtubePlayerRef.current.mute?.()
            else youtubePlayerRef.current.unMute?.()
          } catch {}
        }
      },
      getProvider: () => providerRef.current,
    }))

    useEffect(() => {
      return () => {
        if (youtubeTickRef.current) {
          window.clearInterval(youtubeTickRef.current)
          youtubeTickRef.current = null
        }

        if (vimeoPlayerRef.current) {
          try {
            vimeoPlayerRef.current.destroy()
          } catch {}
          vimeoPlayerRef.current = null
        }

        if (youtubePlayerRef.current?.destroy) {
          try {
            youtubePlayerRef.current.destroy()
          } catch {}
          youtubePlayerRef.current = null
        }
      }
    }, [])

    useEffect(() => {
      providerRef.current = provider

      if (!containerRef.current || !src) return

      if (youtubeTickRef.current) {
        window.clearInterval(youtubeTickRef.current)
        youtubeTickRef.current = null
      }

      if (vimeoPlayerRef.current) {
        try {
          vimeoPlayerRef.current.destroy()
        } catch {}
        vimeoPlayerRef.current = null
      }

      if (youtubePlayerRef.current?.destroy) {
        try {
          youtubePlayerRef.current.destroy()
        } catch {}
        youtubePlayerRef.current = null
      }

      containerRef.current.innerHTML = ''

      if (provider === 'vimeo') {
        const player = new Player(containerRef.current, {
          url: src,
          responsive: true,
          autoplay,
          muted,
          controls,
        })

        vimeoPlayerRef.current = player

        player.ready().then(async () => {
          if (startAt > 0) {
            try {
              await player.setCurrentTime(startAt)
            } catch {}
          }

          try {
            const duration = Number(await player.getDuration()) || 0
            if (duration > 0) {
              durationRef.current = duration
              onDurationRef.current?.(duration, formatDurationLabel(duration))
            }
          } catch {}

          onReadyRef.current?.({ provider: 'vimeo' })
        })

        player.on('play', async () => {
          try {
            const seconds = await player.getCurrentTime()
            onPlayRef.current?.(seconds, durationRef.current)
          } catch {
            onPlayRef.current?.(0, durationRef.current)
          }
        })

        player.on('pause', async () => {
          try {
            const seconds = await player.getCurrentTime()
            onPauseRef.current?.(seconds, durationRef.current)
          } catch {
            onPauseRef.current?.(0, durationRef.current)
          }
        })

        player.on('timeupdate', (data: any) => {
          onTimeUpdateRef.current?.(Number(data?.seconds || 0), durationRef.current)
        })

        player.on('ended', () => {
          onEndedRef.current?.()
        })

        return
      }

      if (provider === 'youtube') {
        const videoId = getYoutubeVideoId(src)
        if (!videoId) return

        const host = document.createElement('div')
        const id = `yt-${Math.random().toString(36).slice(2)}`
        host.id = id
        host.className = 'absolute inset-0 w-full h-full'
        containerRef.current.appendChild(host)

        loadYoutubeApi()
          .then((YT) => {
            youtubePlayerRef.current = new YT.Player(id, {
              videoId,
              playerVars: {
                autoplay: autoplay ? 1 : 0,
                controls: controls ? 1 : 0,
                disablekb: controls ? 0 : 1,
                fs: controls ? 1 : 0,
                modestbranding: 1,
                rel: 0,
                playsinline: 1,
                start: startAt > 0 ? Math.floor(startAt) : undefined,
              },
              events: {
                onReady: (event: any) => {
                  const p = youtubePlayerRef.current
                  const root = containerRef.current
                  const iframe = root?.querySelector('iframe') as HTMLIFrameElement | null

                  if (root) {
                    root.style.width = '100%'
                    root.style.height = '100%'
                  }

                  host.style.width = '100%'
                  host.style.height = '100%'
                  host.style.position = 'absolute'
                  host.style.inset = '0'

                  if (iframe) {
                    iframe.style.position = 'absolute'
                    iframe.style.inset = '0'
                    iframe.style.width = '100%'
                    iframe.style.height = '100%'
                    iframe.style.display = 'block'
                    iframe.style.border = '0'
                  }

                  try {
                    if (muted) p?.mute?.()
                    else p?.unMute?.()
                  } catch {}

                  try {
                    const duration = Number(p?.getDuration?.() || 0)
                    if (duration > 0) {
                      durationRef.current = duration
                      onDurationRef.current?.(duration, formatDurationLabel(duration))
                    }
                  } catch {}

                  if (autoplay) {
                    try {
                      event?.target?.playVideo?.()
                    } catch {}
                  }

                  onReadyRef.current?.({ provider: 'youtube' })

                  youtubeTickRef.current = window.setInterval(() => {
                    const currentPlayer = youtubePlayerRef.current
                    if (!currentPlayer?.getCurrentTime) return
                    try {
                      const seconds = Number(currentPlayer.getCurrentTime() || 0)
                      const duration = Number(currentPlayer.getDuration?.() || durationRef.current || 0)

                      if (duration > 0) {
                        durationRef.current = duration
                      }

                      onTimeUpdateRef.current?.(seconds, durationRef.current)
                    } catch {}
                  }, 2000)
                },
                onStateChange: (event: any) => {
                  const p = youtubePlayerRef.current

                  const getTime = () => {
                    try {
                      return Number(p?.getCurrentTime?.() || 0)
                    } catch {
                      return 0
                    }
                  }

                  const getDuration = () => {
                    try {
                      const duration = Number(p?.getDuration?.() || durationRef.current || 0)

                      if (duration > 0) {
                        durationRef.current = duration
                      }

                      return durationRef.current
                    } catch {
                      return durationRef.current
                    }
                  }

                  if (event.data === YT.PlayerState.PLAYING) {
                    onPlayRef.current?.(getTime(), getDuration())
                  } else if (event.data === YT.PlayerState.PAUSED) {
                    onPauseRef.current?.(getTime(), getDuration())
                  } else if (event.data === YT.PlayerState.ENDED) {
                    onEndedRef.current?.()
                  }
                },
              },
            })
          })
          .catch(() => {})

        return
      }

      const iframe = document.createElement('iframe')
      iframe.src = src
      iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture'
      iframe.allowFullscreen = true
      iframe.referrerPolicy = 'strict-origin-when-cross-origin'
      iframe.className = 'absolute inset-0 w-full h-full block border-0'
      containerRef.current.appendChild(iframe)

      onReadyRef.current?.({ provider })
    }, [
      src,
      provider,
      autoplay,
      muted,
      controls,
      startAt,
    ])

    if (!src) return null

    const roundedClass = getRoundedClass(rounded)

    return (
      <div
        className={cn(
          'relative w-full aspect-video overflow-hidden',
          roundedClass,
          variant === 'glass' && 'border border-white/15 bg-white/5',
          className
        )}
      >
        <div
          ref={containerRef}
          className={cn(
            'absolute inset-0 h-full w-full [&>div]:absolute [&>div]:inset-0 [&>div]:h-full [&>div]:w-full [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:block [&_iframe]:h-full [&_iframe]:w-full',
            frameClassName
          )}
        />
      </div>
    )
  }
)